import { useEffect, useMemo, useState } from 'react';
import { NavItem } from '@root/sanity/sanity.types';
import { urlFor } from '@root/lib/sanity';
import { SanityArrayItem } from '@root/src/types';

import IconX from 'components/IconX';
import TrackedLink from 'components/sanity/TrackedLink';
import { NavTrackingProvider } from './analytics';
import { navItemLink } from './navEvents';
import { isActiveNavLink } from './NavItem';
import PromoNavItem from './PromoNavItem';

type HorizontalHoverNavProps = {
  pathname: string;
  navItems: SanityArrayItem<NavItem>[];
  onClickCapture?: () => void;
  onClose?: () => void;
};

// Matches the dropdown's delay-200 plus duration-200 hide transition below
export const HOVER_NAV_CLOSE_DELAY_MS = 400;

const getColumns = (navItems: SanityArrayItem<NavItem>[]) => {
  const formattedColumns = [];

  for (let i = 0; i < navItems.length; i = i + 2) {
    const first = navItems[i];
    const second = navItems[i + 1];

    if (second && Object.hasOwn(second, 'image')) {
      formattedColumns.push([first], [second]);
    } else {
      formattedColumns.push(navItems.slice(i, i + 2));
    }
  }

  return formattedColumns;
};

const HorizontalHoverNav = ({ pathname, navItems, onClickCapture, onClose }: HorizontalHoverNavProps) => {
  const [isHoverNavOpen, setIsHoverNavOpen] = useState(true);

  useEffect(() => {
    setIsHoverNavOpen(true);
  }, [isHoverNavOpen]);

  const columns = useMemo(() => getColumns(navItems), [navItems]);

  const closeHoverNav = () => {
    setIsHoverNavOpen(false);
    onClose?.();
  };

  return (
    <div
      onClickCapture={onClickCapture}
      className={`fixed
        left-0
        top-80
        hidden
        w-screen
        border-t-1
        border-b-1
        border-gray
        bg-white
        text-black
        opacity-0
        transition-all
        duration-200
        delay-200
        ease-linear
        lg:invisible
        lg:origin-top-left
        lg:group-hover:visible
        lg:group-hover:opacity-100
        lg:group-hover:delay-[0ms]
        ${isHoverNavOpen ? 'lg:flex' : ''}
`}
    >
      <div className="grid flex-grow grid-flow-col grid-rows-1" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        {columns.map((col, index) => (
          <div
            key={`${col[0].title} - ${index}`}
            className={` border-gray ${
              index < columns.length - 1 || (columns.length < 5 && !col.some((section) => section.image))
                ? 'border-r-1'
                : columns.length < 5 && col.some((section) => section.image)
                  ? 'col-start-5 border-l-1'
                  : ''
            }`}
          >
            {col.map((section, index) =>
              section.image ? (
                <div className="flex h-full flex-col justify-center px-8 text-center" key={`${section.href} - ${index}`}>
                  <NavTrackingProvider item={{ ...navItemLink(section, navItems), alt: section.image.alt, elementType: 'callout_image' }}>
                    <PromoNavItem
                      title={section.title}
                      href={section.href}
                      image={urlFor(section.image.asset).url()}
                      alt={section.image.alt ?? ''}
                    />
                  </NavTrackingProvider>
                </div>
              ) : (
                <div
                  className={`flex flex-col py-20 pl-16 ${index !== col.length - 1 ? 'border-b-1 border-gray' : ''}`}
                  key={`${section.href}-${index}`}
                >
                  <NavTrackingProvider item={navItemLink(section, navItems)}>
                    <TrackedLink
                      key={section.title}
                      nav
                      href={section.href}
                      onClick={closeHoverNav}
                      className={`py-4 uppercase font-semibold hover:underline ${
                        isActiveNavLink(pathname, section.href, 'Sub') ? 'text-gold-darker' : ''
                      }`}
                    >
                      {section.title}
                    </TrackedLink>

                    {section.children &&
                      section.children.map((link, _, siblings) => (
                        <TrackedLink
                          key={`${link.title} - ${link.href}`}
                          nav
                          navItem={navItemLink(link, siblings)}
                          href={link.href}
                          onClick={closeHoverNav}
                          className={`py-4 hover:underline ${isActiveNavLink(pathname, link.href, 'Sub Sub') ? 'text-gold-darker' : ''}`}
                        >
                          {link.title}
                        </TrackedLink>
                      ))}
                  </NavTrackingProvider>
                </div>
              ),
            )}
          </div>
        ))}
      </div>
      <div className="mt-8 mr-16">
        <button onClick={closeHoverNav}>
          <IconX className="h-32 w-32 text-gray" />
        </button>
      </div>
    </div>
  );
};

export default HorizontalHoverNav;
